/**
 * Staging — PF v2 native configuration UI (Wave 3, the last Kind-B module).
 *
 * Rewrites the legacy `StyleSelectionStepController` → `StagingFlow` tree onto the
 * PF v2 store + file hub. Durable state lives in `productConfigs["${cartItemId}|
 * staging"]` (Pattern A); ephemeral working state (cursor + popups) lives in the
 * module-scoped `StagingWorkingState` context (E1, outside the persisted store);
 * the room set is reconciled from the file-hub visual feed by `useStagingRoomSync`
 * (E2, ADD-only). No RoomInventory reducer, no RoomAPI pairing, no adapter handle,
 * no ConfigSessionShim / StagingContextShim / DynamicFlow, no cart side-effect.
 *
 * Crash-by-construction (§1.5, RISK-S8): rooms are read SYNCHRONOUSLY from the
 * store (hydration is a `set()` before mount) — the reducer-commit gap that
 * produced the 2026-07-03 white-screen is gone. The lifted leaves still keep every
 * `activeRoom?.` / `if (!activeRoom) return` guard (a room can be removed
 * while a popup is open).
 *
 * Dropped vs. the legacy controller: the `roomItemOpenPopup` events and the
 * DynamicFlow back-nav effect. `ENTER_ORDER_STAGING_PAGE_SCREEN` is no longer
 * dropped — the configuration page announces this tab's activation for it. `StepWrapper` (a `PurchaseFlow/*` import) →
 * in-component title/subtitle header.
 *
 * Server-state catalogues (`VirtualFurnitureData` / `VirtualFurnitureUpgrades` /
 * `StagingDecorations`) are React Query behind `components/contexts/*` providers
 * (ALLOWED, shared with Gallery/StagingSelection) mounted in this module's own
 * subtree. ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate).
 */

import React from 'react'
import { Fade, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { StagingDecorationsProvider } from 'components/contexts/StagingDecorations.context'
import { VirtualFurnitureDataContextProvider } from 'components/contexts/VirtualFurnitureData.context'
import { VirtualFurnitureUpgradesContextProvider } from 'components/contexts/VirtualFurnitureUpgradesContext'

import type { CartMutationHandler } from '../../adapters/bridge'
import { StagingProcessingVisuals } from './StagingProcessingVisuals.component'
import { StagingWorkingStateProvider, useStagingWorkingState } from './StagingWorkingState.context'
import { useStagingRoomSync } from './useStagingRoomSync'
import { usePendingRoomCount } from './usePendingRoomCount'
import { RoomSelection } from './components/RoomSelection.component'
import { VisualStyleSelectionsContainer } from './components/VisualStyleSelectionsContainer.component'

export interface StagingProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — staging emits no cart mutations
   *  natively (the legacy renovation cart-extra is dropped, DF-staging-cart-renovation). */
  onCartMutation?: CartMutationHandler
}

/** Inner body — runs the room-set reconcile and switches between the empty state
 *  and the staging flow. Mounted inside all module providers. */
const StagingBody: React.FC<{ cartItemId: CartItemId }> = ({ cartItemId }) => {
  const { t } = useTranslation(['style_selection_order_step'])

  // E2 ADD-only reconcile — feed → room-set membership.
  useStagingRoomSync(cartItemId)

  // The reconcile is gated on quiescence, so a photo mid-upload has no room yet.
  const pendingRooms = usePendingRoomCount(cartItemId)

  const { allRooms } = useStagingWorkingState()
  const hasRooms = allRooms.length > 0

  return (
    <Fade in>
      <Stack direction="column" gap={2} alignItems="stretch" justifyContent="flex-start">

        <Stack gap={0.4}>

          <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
            {t('style_selection_order_step:title')}
          </Typography>

          <Typography variant="text-sm" color={GRAY_700}>
            {t('style_selection_order_step:description')}
          </Typography>

        </Stack>

        {!hasRooms && <StagingProcessingVisuals />}

        {/* Side by side only when there is room for both. The room list is sized by
            its thumbnails (≈290px floor) and the editor needs roughly 400px before
            its button rows wrap to one item per line, so below `lg` the pair is
            stacked instead of squeezed — at a 900px viewport, where the summary
            column first appears and the content column is at its narrowest, the
            editor was down to ~200px. */}
        {hasRooms &&
          <Stack
            width="100%"
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'flex-start' }}
            gap={2.4}
          >

            <RoomSelection pendingRooms={pendingRooms} />

            <VisualStyleSelectionsContainer />

          </Stack>
        }

      </Stack>
    </Fade>
  )
}

/**
 * @component Staging
 * Native staging configuration. Mounts the server-state catalogues + the module
 * working-state context, then renders the reconcile-driven flow.
 */
export const Staging: React.FC<StagingProps> = ({ cartItemId }) => (
  <VirtualFurnitureDataContextProvider>
    <VirtualFurnitureUpgradesContextProvider>
      <StagingDecorationsProvider>
        <StagingWorkingStateProvider cartItemId={cartItemId}>

          <StagingBody cartItemId={cartItemId} />

        </StagingWorkingStateProvider>
      </StagingDecorationsProvider>
    </VirtualFurnitureUpgradesContextProvider>
  </VirtualFurnitureDataContextProvider>
)
